import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Collapse,
  ButtonBase,
} from "@mui/material";
import LoginOutlinedIcon from "@mui/icons-material/LoginOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import {
  PermIdentity as RetailIcon,
  Business as BusinessIcon,
  PersonOutline as EmployeeIcon,
  LockOutlined as LockIcon,
  LocalShipping as DeliveryIcon,
} from "@mui/icons-material";
import { format } from "date-fns";
import { Colors } from "../../../configs";
import { pxToRem } from "../../../util";
import { formatDate } from "../../../util/common-functions";
import { ICustomerBill } from "../../../typings/interfaces/responses/customer-bills-response";
import { ICustomerResponse } from "../../../typings";

interface NfcScanResultModalProps {
  open: boolean;
  customer: ICustomerResponse;
  pendingBills: ICustomerBill[];
  onClose: () => void;
  onCheckIn: () => void;
  onGoToHistory: () => void;
  onBillClick: (bill: ICustomerBill) => void;
}

const formatPhoneNumber = (raw: string | null | undefined): string => {
  if (!raw) return "";
  let digits = raw.replace(/\D/g, "");
  if (digits.length === 11 && digits.startsWith("1")) digits = digits.slice(1);
  if (digits.length !== 10) return raw;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

const IconBadge: React.FC<{ bg: string; children: React.ReactNode }> = ({ bg, children }) => (
  <Box
    sx={{
      backgroundColor: "rgba(255,255,255,0.9)",
      borderRadius: "50%",
      p: "0.25rem",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}
  >
    <Box
      sx={{
        backgroundColor: bg,
        borderRadius: "4px",
        p: "4px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: 17,
        height: 17,
      }}
    >
      {children}
    </Box>
  </Box>
);

const CustomerBadges: React.FC<{ customer: ICustomerResponse }> = ({ customer }) => {
  const type = customer.customerTypePref?.[0];
  const isActive = customer.accountStatus === "ACTIVE";
  const isLocked = customer.accountStatus === "LOCKED";
  const inactive = Colors.ACCENT_GRAY ?? "#BDBDBD";

  let typeBg = isActive ? Colors.SECONDARY : inactive;
  let TypeIcon: React.ElementType = BusinessIcon;

  if (isLocked) {
    typeBg = "#FFBF00";
    TypeIcon = LockIcon;
  } else if (type === "RETAIL") {
    typeBg = isActive ? "#2E7D32" : inactive;
    TypeIcon = RetailIcon;
  } else if (type === "EMPLOYEE") {
    typeBg = isActive ? "#1976D2" : inactive;
    TypeIcon = EmployeeIcon;
  }

  const deliveryColor = isActive ? "#FFBF00" : inactive;

  return (
    <>
      {customer.isDeliveryEnabled && (
        <IconBadge bg={deliveryColor}>
          <DeliveryIcon sx={{ color: "#fff", fontSize: "1rem" }} />
        </IconBadge>
      )}
      <IconBadge bg={typeBg}>
        <TypeIcon sx={{ color: "#fff", fontSize: "1rem" }} />
      </IconBadge>
      {customer.paymentType === "Monthly Charge" && (
        <IconBadge bg={isActive ? "#9C27B0" : inactive}>
          <CalendarTodayIcon sx={{ color: "#fff", fontSize: "1rem" }} />
        </IconBadge>
      )}
      {customer.paymentType === "Card on File" && (
        <IconBadge bg={isActive ? "#E91E63" : inactive}>
          <CreditCardIcon sx={{ color: "#fff", fontSize: "1rem" }} />
        </IconBadge>
      )}
    </>
  );
};

const Chip: React.FC<{ label: string; bg: string; color: string }> = ({ label, bg, color }) => (
  <Box
    sx={{
      px: pxToRem(7),
      py: pxToRem(2),
      borderRadius: pxToRem(20),
      backgroundColor: bg,
      display: "inline-flex",
      flexShrink: 0,
    }}
  >
    <Typography sx={{ fontSize: pxToRem(11), fontWeight: 600, color, lineHeight: 1.4 }}>
      {label}
    </Typography>
  </Box>
);

const PaymentChip: React.FC<{ status: string }> = ({ status }) => {
  const map: Record<string, { label: string; bg: string; color: string }> = {
    NOT_PAID: { label: "Not Paid", bg: "#FEE2E2", color: "#B91C1C" },
    PARTIALLY_PAID: { label: "Partial", bg: "#FEF9C3", color: "#92400E" },
    FULLY_PAID: { label: "Paid", bg: "#DCFCE7", color: "#15803D" },
  };
  const cfg = map[status];
  return cfg ? <Chip {...cfg} /> : null;
};

const PickupChip: React.FC<{ status: string }> = ({ status }) => {
  const map: Record<string, { label: string; bg: string; color: string }> = {
    NOT_PICKED_UP: { label: "Not Picked Up", bg: "#FFEDD5", color: "#C2410C" },
    PARTIALLY_PICKED_UP: { label: "Partial Pickup", bg: "#DBEAFE", color: "#1D4ED8" },
  };
  const cfg = map[status];
  return cfg ? <Chip {...cfg} /> : null;
};

const NfcScanResultModal: React.FC<NfcScanResultModalProps> = ({
  open,
  customer,
  pendingBills,
  onClose,
  onCheckIn,
  onGoToHistory,
  onBillClick,
}) => {
  const [pickupsExpanded, setPickupsExpanded] = useState(false);

  useEffect(() => {
    if (!open) setPickupsExpanded(false);
  }, [open]);

  const address = customer.notWillingToProvide
    ? "—"
    : [
        customer.personalAddressLine1,
        customer.personalAddressLine2,
        customer.personalCity,
        customer.personalState,
        customer.personalZip,
      ]
        .filter((p) => p && p.trim())
        .join(", ") || "—";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: pxToRem(500),
          maxWidth: "92vw",
          borderRadius: pxToRem(18),
          boxShadow: "0 24px 60px rgba(0,0,0,0.15)",
          overflow: "hidden",
          backgroundColor: Colors.WHITE,
        },
      }}
    >
      <DialogContent sx={{ p: 0, "&:last-child": { pb: 0 } }}>

        {/* Header */}
        <Box
          sx={{
            px: pxToRem(20),
            pt: pxToRem(18),
            pb: pxToRem(14),
            borderBottom: "1px solid #F0F0F0",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box>
            <Typography
              sx={{
                fontSize: pxToRem(10),
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.18em",
                color: Colors.PRIMARY,
                lineHeight: 1.3,
              }}
            >
              NFC Scan
            </Typography>
            <Typography
              sx={{
                fontSize: pxToRem(19),
                fontWeight: 600,
                color: Colors.TEXT_GRAY_DARK,
                lineHeight: 1.2,
              }}
            >
              Customer Found
            </Typography>
          </Box>
          <ButtonBase
            onClick={onClose}
            sx={{
              width: pxToRem(36),
              height: pxToRem(36),
              borderRadius: "50%",
              border: "1px solid #E0E0E0",
              color: Colors.TEXT_GRAY_DARK,
              flexShrink: 0,
              "&:hover": { backgroundColor: "#F5F5F5" },
            }}
          >
            <CloseIcon sx={{ fontSize: pxToRem(16) }} />
          </ButtonBase>
        </Box>

        {/* Customer Card — matches SearchResultCad format */}
        <Box sx={{ px: pxToRem(16), pt: pxToRem(14), pb: pxToRem(4) }}>
          <Box
            sx={{
              background: Colors.WHITE,
              border: "1px solid #E6E6E6",
              borderRadius: "0.857rem",
              padding: "0.9rem 1rem",
              boxShadow: "0 0.285rem 0.857rem rgba(0,0,0,0.08)",
              position: "relative",
            }}
          >
            {/* Badges — top right */}
            <Box
              sx={{
                position: "absolute",
                top: "0.5rem",
                right: "0.5rem",
                display: "flex",
                alignItems: "center",
                gap: "0.25rem",
                zIndex: 2,
              }}
            >
              <CustomerBadges customer={customer} />
            </Box>

            <Typography
              sx={{
                fontSize: "1.2rem",
                fontWeight: 700,
                color: Colors.TEXT_GRAY_DARK,
                pr: pxToRem(70),
              }}
            >
              {customer.firstName} {customer.lastName} | {customer.id}
            </Typography>

            <Typography sx={{ fontSize: "1.14rem", color: Colors.TEXT_GRAY_DARK }}>
              {address}
            </Typography>

            <Box sx={{ display: "flex", justifyContent: "space-between", mt: pxToRem(2) }}>
              <Typography sx={{ fontSize: "1.05rem", color: Colors.SECONDARY }}>
                {formatPhoneNumber(customer.mobilePhone)}
              </Typography>
              <Typography sx={{ fontSize: "1.05rem", color: Colors.ACCENT_GRAY_DISABLED }}>
                Member since:{" "}
                {customer.createdTime ? formatDate(customer.createdTime) : "N/A"}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Action Buttons */}
        <Box
          sx={{
            px: pxToRem(16),
            pt: pxToRem(14),
            pb: pxToRem(16),
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: pxToRem(12),
          }}
        >
          <ButtonBase
            onClick={onCheckIn}
            sx={{
              height: pxToRem(64),
              borderRadius: pxToRem(14),
              backgroundColor: Colors.PRIMARY,
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: pxToRem(8),
              fontWeight: 600,
              fontSize: pxToRem(15),
              transition: "background-color 0.15s ease",
              "&:hover": { backgroundColor: Colors.PRIMARY_DARK },
            }}
          >
            <LoginOutlinedIcon sx={{ fontSize: pxToRem(20) }} />
            Drop Off
          </ButtonBase>
          <ButtonBase
            onClick={onGoToHistory}
            sx={{
              height: pxToRem(64),
              borderRadius: pxToRem(14),
              border: `1.5px solid ${Colors.SECONDARY}`,
              color: Colors.SECONDARY,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: pxToRem(8),
              fontWeight: 600,
              fontSize: pxToRem(15),
              transition: "background-color 0.15s ease",
              "&:hover": { backgroundColor: `${Colors.SECONDARY}12` },
            }}
          >
            <ReceiptLongOutlinedIcon sx={{ fontSize: pxToRem(20) }} />
            Pick Up
          </ButtonBase>
        </Box>

        {/* Pending Orders — collapsible */}
        {pendingBills.length > 0 ? (
          <Box sx={{ borderTop: "1px solid #F0F0F0" }}>
            <ButtonBase
              onClick={() => setPickupsExpanded((p) => !p)}
              sx={{
                width: "100%",
                px: pxToRem(16),
                py: pxToRem(11),
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                "&:hover": { backgroundColor: "#FAFAFA" },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: pxToRem(7) }}>
                <Inventory2OutlinedIcon
                  sx={{ fontSize: pxToRem(15), color: Colors.ACCENT_GRAY_DISABLED }}
                />
                <Typography
                  sx={{
                    fontSize: pxToRem(11),
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "0.1em",
                    color: Colors.ACCENT_GRAY_DISABLED,
                  }}
                >
                  Pending Orders ({pendingBills.length})
                </Typography>
              </Box>
              <KeyboardArrowDownIcon
                sx={{
                  fontSize: pxToRem(20),
                  color: Colors.ACCENT_GRAY_DISABLED,
                  transition: "transform 0.2s ease",
                  transform: pickupsExpanded ? "rotate(180deg)" : "rotate(0deg)",
                }}
              />
            </ButtonBase>

            <Collapse in={pickupsExpanded}>
              <Box
                sx={{
                  px: pxToRem(12),
                  pb: pxToRem(12),
                  display: "flex",
                  flexDirection: "column",
                  gap: pxToRem(7),
                  maxHeight: pxToRem(280),
                  overflowY: "auto",
                  "&::-webkit-scrollbar": { width: 4 },
                  "&::-webkit-scrollbar-thumb": {
                    backgroundColor: "#D0D0D0",
                    borderRadius: 4,
                  },
                }}
              >
                {pendingBills.map((bill) => (
                  <ButtonBase
                    key={bill.id}
                    onClick={() => onBillClick(bill)}
                    sx={{
                      width: "100%",
                      borderRadius: pxToRem(12),
                      border: "1px solid #F0F0F0",
                      px: pxToRem(14),
                      py: pxToRem(11),
                      textAlign: "left",
                      display: "flex",
                      alignItems: "center",
                      gap: pxToRem(12),
                      transition: "all 0.15s ease",
                      "&:hover": {
                        borderColor: `${Colors.PRIMARY}4D`,
                        backgroundColor: `${Colors.PRIMARY}08`,
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: pxToRem(34),
                        height: pxToRem(34),
                        borderRadius: pxToRem(8),
                        backgroundColor: "#F4F4F4",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <Inventory2OutlinedIcon
                        sx={{ fontSize: pxToRem(17), color: Colors.TEXT_GRAY_DARK }}
                      />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: pxToRem(6),
                          mb: pxToRem(4),
                          flexWrap: "wrap",
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: pxToRem(13),
                            fontWeight: 700,
                            color: Colors.TEXT_GRAY_DARK,
                          }}
                        >
                          #{bill.billId}
                        </Typography>
                        <PaymentChip status={bill.paymentFilterStatus} />
                        <PickupChip status={bill.pickingFilterStatus} />
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "center", gap: pxToRem(6) }}>
                        <Typography sx={{ fontSize: pxToRem(11), color: Colors.ACCENT_GRAY_DISABLED }}>
                          {bill.createdTime
                            ? format(new Date(bill.createdTime), "MMM d, yyyy")
                            : "—"}
                        </Typography>
                        <Typography sx={{ fontSize: pxToRem(11), color: Colors.ACCENT_GRAY_DISABLED }}>·</Typography>
                        <Typography sx={{ fontSize: pxToRem(11), color: Colors.ACCENT_GRAY_DISABLED }}>
                          {bill.totalItemCount} item{bill.totalItemCount !== 1 ? "s" : ""}
                        </Typography>
                        <Typography sx={{ fontSize: pxToRem(11), color: Colors.ACCENT_GRAY_DISABLED }}>·</Typography>
                        <Typography
                          sx={{
                            fontSize: pxToRem(12),
                            fontWeight: 600,
                            color: Colors.TEXT_GRAY_DARK,
                          }}
                        >
                          ${bill.billAmount?.toFixed(2)}
                        </Typography>
                      </Box>
                    </Box>
                    <ChevronRightIcon
                      sx={{ fontSize: pxToRem(18), color: "#D0D0D0", flexShrink: 0 }}
                    />
                  </ButtonBase>
                ))}
              </Box>
            </Collapse>
          </Box>
        ) : (
          <Box sx={{ px: pxToRem(16), pb: pxToRem(18), textAlign: "center" }}>
            <Typography sx={{ fontSize: pxToRem(13), color: Colors.ACCENT_GRAY_DISABLED }}>
              No pending orders found.
            </Typography>
          </Box>
        )}

      </DialogContent>
    </Dialog>
  );
};

export default NfcScanResultModal;
